import * as React from 'react';
import { useEffect, useState } from 'react';
import { FormField, Multiselect, SpaceBetween } from '@amzn/awsui-components-react-v3';
import { LfPrincipalTypeSelector } from 'src/components/catalog/datasetdetail/accessSelectModal/lfPrincipalTypeSelector';
import { FGA_POLICY_TYPE_PREFIX, LAKEFORMATION_RESOURCE, REDSHIFT_RESOURCE } from 'src/commons/constants';
import { CartAccessItem } from 'src/components/common/checkout/common';
import { getConsumerDataLakePrincipal, getFoundryRoleARN } from 'src/commons/common';
import { IdInfo } from 'src/components/utils/arnUtil';
import { createFgaPolicyViewLink } from 'src/routes';
import { ResourceSummaryTable } from 'src/components/catalog/datasetdetail/accessSelectModal/resourceSummaryTable';
import { PermissionDropdownOption } from 'src/components/catalog/datasetdetail/datasetDetailHelpers';
import { GalaxiGroupDropdown } from 'src/components/catalog/datasetdetail/accessSelectModal/galaxiGroupDropdown';
import { AccountAssociationCheckboxes } from 'src/components/catalog/datasetdetail/accessSelectModal/accountAssociationCheckboxes';

export interface FgapAccessSelectorProps {
  activeWorkspace: any;
  idInfo: IdInfo;
  loading: boolean;
  options: any;
  setCartItemsToAdd: any;
  isDWaaSWorkspace?: boolean;
  galaxiGroupOptions?: any;
}

export const FgapAccessSelector = (props: FgapAccessSelectorProps) => {
  const [selectedPermissions, setSelectedPermissions] = useState<PermissionDropdownOption[]>([]);
  const [disableAccountLevelAccess, setDisableAccountLevelAccess] = useState<boolean>(true);
  const [resourceSummaryItems, setResourceSummaryItems] = useState<any[]>([]);
  const [principal, setPrincipal] = useState<string>();
  const [selectedGalaxiGroup, setSelectedGalaxiGroup] = useState(undefined);
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);

  let flattenFineGrainAccess = (selectedPermission, principal): CartAccessItem => {
    return {
      catalogDisplayName: selectedPermission.catalogName,
      catalogName: selectedPermission.catalogName,
      catalogId: selectedPermission.catalogId,
      clusterId: selectedPermission.clusterId,
      bucketName: selectedPermission.bucketName,
      databaseName: selectedPermission.databaseName,
      schemaName: selectedPermission.schemaName,
      tableName: selectedPermission.tableName,
      datasetName: selectedPermission.datasetName ?? selectedPermission.tableName,
      region: props.idInfo.region,
      dataOwnerRole: getFoundryRoleARN(props.idInfo?.region, selectedPermission.catalogId),
      owner: selectedPermission.ownerId,
      itemType: FGA_POLICY_TYPE_PREFIX,
      permissionId: selectedPermission.permissionId,
      fgapName: selectedPermission.fgapName,
      id: selectedPermission.id,
      dataLakePrincipal: principal,
    };
  };

  useEffect(() => {
    let res = [];

    if (selectedPermissions == undefined) {
      setResourceSummaryItems([]);
      return;
    }

    for (let selectedPermission of selectedPermissions) {
      if (selectedPermission.type == FGA_POLICY_TYPE_PREFIX) {
        res.push({
          type: FGA_POLICY_TYPE_PREFIX,
          resource: `${selectedPermission.fgapName}`,
          resourceLink: createFgaPolicyViewLink(selectedPermission.id),
        });
      }
    }
    setResourceSummaryItems(res);
  }, [selectedPermissions]);

  // Update the cart item whenever the principal is updated
  useEffect(() => {
    let cartItems: CartAccessItem[] = [];
    for (let permission of selectedPermissions) {
      let datalakePrincipal =
        props.idInfo?.clusterId != null
          ? getConsumerDataLakePrincipal(REDSHIFT_RESOURCE, undefined, props.activeWorkspace.accountId)
          : getConsumerDataLakePrincipal(LAKEFORMATION_RESOURCE, principal, props.activeWorkspace.accountId);
      let cartItem = flattenFineGrainAccess(permission, datalakePrincipal);
      cartItems.push(cartItem);
    }
    props.setCartItemsToAdd(cartItems);
  }, [principal]);

  const buildDWaaSCartItems = (permissions: PermissionDropdownOption[], roles: string[]): CartAccessItem[] => {
    const isRedshift = props.idInfo?.clusterId != null;
    const cartItems: CartAccessItem[] = [];
    for (let permission of permissions) {
      if (isRedshift) {
        cartItems.push(
          flattenFineGrainAccess(
            permission,
            getConsumerDataLakePrincipal(REDSHIFT_RESOURCE, undefined, props.activeWorkspace.accountId),
          ),
        );
      } else {
        for (let role of roles) {
          cartItems.push(
            flattenFineGrainAccess(
              permission,
              getConsumerDataLakePrincipal(LAKEFORMATION_RESOURCE, role, props.activeWorkspace.accountId),
            ),
          );
        }
      }
    }
    return cartItems;
  };

  const handleChange = (e) => {
    setSelectedPermissions(e.detail.selectedOptions);
    setDisableAccountLevelAccess(e.detail.selectedOptions.filter((option) => option.workspaceAccess).length > 0);

    if (props.isDWaaSWorkspace) {
      props.setCartItemsToAdd(buildDWaaSCartItems(e.detail.selectedOptions, selectedRoles));
      return;
    }

    let cartItems: CartAccessItem[] = [];
    for (let permission of e.detail.selectedOptions) {
      let datalakePrincipal =
        props.idInfo?.clusterId != null
          ? getConsumerDataLakePrincipal(REDSHIFT_RESOURCE, undefined, props.activeWorkspace.accountId)
          : getConsumerDataLakePrincipal(LAKEFORMATION_RESOURCE, principal, props.activeWorkspace.accountId);
      cartItems.push(flattenFineGrainAccess(permission, datalakePrincipal));
    }
    props.setCartItemsToAdd(cartItems);
  };

  const handleDWaaSWorkspaceChange = (e) => {
    setSelectedGalaxiGroup(e.detail.selectedOption);
    setSelectedRoles([]);
    props.setCartItemsToAdd([]);
  };

  const handleRoleSelectionChange = (roles: string[]) => {
    setSelectedRoles(roles);
    props.setCartItemsToAdd(buildDWaaSCartItems(selectedPermissions, roles));
  };

  return (
    <SpaceBetween size={'m'} direction={'vertical'}>
      <FormField
        label='Select permission(s)'
        description={'Select fine grain access policies permission defined by the data provider'}
      >
        <Multiselect
          options={props.options}
          selectedAriaLabel='Selected'
          selectedOptions={selectedPermissions}
          onChange={handleChange}
          empty='No access options'
          statusType={props.loading ? 'loading' : 'finished'}
          loadingText='Loading access options'
        />
      </FormField>

      {selectedPermissions?.length > 0 && props.isDWaaSWorkspace && (
        <GalaxiGroupDropdown
          selectedOption={selectedGalaxiGroup}
          onChange={handleDWaaSWorkspaceChange}
          options={props.galaxiGroupOptions}
          errorText={undefined}
        />
      )}
      {selectedPermissions?.length > 0 && props.isDWaaSWorkspace && selectedGalaxiGroup && !props.idInfo?.clusterId && (
        <AccountAssociationCheckboxes
          accountAssociations={selectedGalaxiGroup['accountAssociations'] ?? []}
          selectedRoles={selectedRoles}
          onChange={handleRoleSelectionChange}
          errorText={selectedRoles.length === 0 ? 'Select at least one IAM role' : undefined}
        />
      )}
      {selectedPermissions?.length > 0 && !props.isDWaaSWorkspace && (
        <LfPrincipalTypeSelector
          disableAccountLevelAccess={disableAccountLevelAccess}
          loading={props.loading}
          principal={principal}
          setPrincipal={setPrincipal}
        />
      )}
      {resourceSummaryItems?.length > 0 &&
        (!props.isDWaaSWorkspace || selectedRoles.length > 0 || props.idInfo?.clusterId) && (
          <ResourceSummaryTable loading={props.loading} items={resourceSummaryItems} />
        )}
    </SpaceBetween>
  );
};
